import React, { useMemo, useState } from "react";
import { rateColumns, goalieRateColumns } from "../constants/ratings";
import { getRatingColor, getRatingRange, getGoalieRatingRange } from "../utils/helpers";
import { filterSkaters, filterGoalies } from "../utils/filterPlayers";
import { isFreeAgentPlayer } from "../utils/rosterHelpers";
import PlayerNameBadges from "./PlayerNameBadges";

const GOALIE_RATE_NAMES = goalieRateColumns.map((col) => col.name);

function parseSalary(salaries, player, wishlistSalaries) {
  const raw = wishlistSalaries[player.id];
  if (raw) return parseInt(String(raw).replace(/[^0-9]/g, ""), 10) || 0;
  if (salaries[player.id]) return parseInt(String(salaries[player.id]).replace(/[^0-9]/g, ""), 10) || 0;
  return parseInt(player.salary, 10) || 0;
}

function formatMoney(value) {
  return `$${Number(value || 0).toLocaleString("en-US")}`;
}

function RatingStrip({ player, rateMin, rateMax, isGoalie = false }) {
  const cols = isGoalie ? GOALIE_RATE_NAMES : rateColumns;

  return (
    <div className="wishlist-ratings-scroll">
      <div className={`player-ratings wishlist-ratings ${isGoalie ? "goalie-ratings" : "skater-ratings"}`}>
        {cols.map((name) => {
          const value = player.rates?.[name] ?? player[name] ?? 0;
          const backgroundColor = getRatingColor(value, rateMin, rateMax);
          return (
            <div
              key={name}
              className="rating-item"
              data-rating={name}
              style={{ backgroundColor, borderColor: backgroundColor }}
            >
              <span className="rating-label">{name}</span>
              <span className="rating-value">{value}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DiffStrip({ playerA, playerB, isGoalie = false }) {
  const cols = isGoalie ? GOALIE_RATE_NAMES : rateColumns;

  return (
    <div className="wishlist-ratings-scroll">
      <div className={`player-ratings wishlist-ratings wishlist-ratings-diff ${isGoalie ? "goalie-ratings" : "skater-ratings"}`}>
        {cols.map((name) => {
          const aVal = playerA.rates?.[name] ?? playerA[name] ?? 0;
          const bVal = playerB.rates?.[name] ?? playerB[name] ?? 0;
          const diff = aVal - bVal;
          const cls = diff > 0 ? "positive" : diff < 0 ? "negative" : "neutral";
          return (
            <div key={name} className={`rating-item wishlist-diff-chip ${cls}`}>
              <span className="rating-label">{name}</span>
              <span className="rating-value">{diff === 0 ? "0" : `${diff > 0 ? "+" : ""}${diff}`}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WishlistCard({
  player,
  isGoalie,
  wishlistSalaries,
  setWishlistSalaries,
  onRemove,
  onGoToLineup,
  onAddToLineup,
  lineupAssignment,
  rateMin,
  rateMax,
}) {
  return (
    <div className={`wishlist-card ${lineupAssignment ? "in-lineup" : ""}`}>
      <div className="wishlist-card-header">
        <div className="wishlist-card-title">
          <div className="wishlist-card-name player-name-row player-name-row-inline">
            <span className="player-name-text">{player.name}</span>
            <PlayerNameBadges player={player} size="small" showPlayerType={!isGoalie} />
          </div>
          <div className="wishlist-card-meta">
            <p className="wishlist-card-meta-line">
              <span>{isGoalie ? "Goalie" : player.positionDisplay}</span>
              <span className="wishlist-card-meta-sep">·</span>
              <span className="wishlist-card-meta-team">{player.team === "N/A" ? "FA" : player.team}</span>
            </p>
            <p className="wishlist-card-meta-line wishlist-card-meta-stats">
              {player.age ? <span>Age {player.age}</span> : null}
              {player.OV ? <span>OV {player.OV}</span> : null}
              {lineupAssignment ? (
                <span className="wishlist-lineup-badge">Lineup: {lineupAssignment}</span>
              ) : null}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="btn btn-secondary btn-sm wishlist-card-remove"
          onClick={() => onRemove(player, isGoalie)}
        >
          Remove
        </button>
      </div>
      <div className="wishlist-card-footer">
        <div className="wishlist-card-actions">
          <input
            type="text"
            inputMode="numeric"
            className="wishlist-salary-input"
            placeholder="Offer $"
            value={
              wishlistSalaries[player.id]
                ? formatMoney(Number(String(wishlistSalaries[player.id]).replace(/[^0-9]/g, "")))
                : player.salary
                  ? formatMoney(player.salary)
                  : ""
            }
            onChange={(e) => {
              const raw = e.target.value.replace(/[^0-9]/g, "");
              setWishlistSalaries((prev) => ({ ...prev, [player.id]: raw }));
            }}
          />
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={(e) => onAddToLineup(player, e)}
          >
            Add to Lineup
          </button>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => onGoToLineup()}>
            View Lineup
          </button>
        </div>
        <RatingStrip player={player} rateMin={rateMin} rateMax={rateMax} isGoalie={isGoalie} />
      </div>
    </div>
  );
}

export default function FreeAgentWishlist({
  wishlistPlayers,
  wishlistGoalies,
  wishlistSalaries,
  setWishlistSalaries,
  setWishlistPlayers,
  setWishlistGoalies,
  players,
  goalies,
  forwards,
  defensemen,
  lineupGoalies,
  salaries,
  retainedSalary,
  salaryCap,
  toggleWishlist,
  isInWishlist,
  onGoToLineup,
  onAddToLineup,
  getLineupAssignmentDisplay,
  skaterRatingRange: skaterRatingRangeProp,
  goalieRatingRange: goalieRatingRangeProp,
}) {
  const [subTab, setSubTab] = useState("list");
  const [browseSearch, setBrowseSearch] = useState("");
  const [browsePosition, setBrowsePosition] = useState("all");
  const [browseFaStatus, setBrowseFaStatus] = useState("all");
  const [browsePlayerType, setBrowsePlayerType] = useState("all");
  const [browseSortColumn, setBrowseSortColumn] = useState("OV");
  const [browseSortDirection, setBrowseSortDirection] = useState("desc");
  const [selectedComparisons, setSelectedComparisons] = useState({});
  const [headToHeadA, setHeadToHeadA] = useState("");
  const [headToHeadB, setHeadToHeadB] = useState("");

  const allWishlist = useMemo(
    () => [...wishlistPlayers, ...wishlistGoalies],
    [wishlistPlayers, wishlistGoalies]
  );

  const freeAgentSkaters = useMemo(
    () => players.filter((p) => isFreeAgentPlayer(p)),
    [players]
  );

  const freeAgentGoalies = useMemo(
    () => goalies.filter((g) => isFreeAgentPlayer(g)),
    [goalies]
  );

  const browseFilterParams = useMemo(
    () => ({
      position: browsePosition,
      team: "all",
      searchTerm: browseSearch,
      sortColumn: browseSortColumn,
      sortDirection: browseSortDirection,
      faStatus: browseFaStatus,
      playerType: browsePlayerType,
    }),
    [browsePosition, browseSearch, browseSortColumn, browseSortDirection, browseFaStatus, browsePlayerType]
  );

  const isBrowsingGoalies = browsePosition === "Goalie";

  const browseSortOptions = useMemo(() => {
    const base = ["name", "team", "age", "salary"];
    return isBrowsingGoalies
      ? base.concat(goalieRateColumns.map((c) => c.name))
      : base.concat(["playerType"], rateColumns);
  }, [isBrowsingGoalies]);

  const browseList = useMemo(() => {
    if (isBrowsingGoalies) {
      return filterGoalies(freeAgentGoalies, browseFilterParams);
    }
    return filterSkaters(freeAgentSkaters, browseFilterParams);
  }, [isBrowsingGoalies, freeAgentSkaters, freeAgentGoalies, browseFilterParams]);

  const lineupSkaters = useMemo(
    () => [
      ...forwards.flatMap((line) => Object.values(line).filter(Boolean)),
      ...defensemen.flatMap((line) => Object.values(line).filter(Boolean)),
    ],
    [forwards, defensemen]
  );

  const lineupGoalieList = useMemo(() => lineupGoalies.filter(Boolean), [lineupGoalies]);

  const computedSkaterRange = useMemo(() => getRatingRange(players, rateColumns), [players]);
  const computedGoalieRange = useMemo(
    () => getGoalieRatingRange(goalies, GOALIE_RATE_NAMES),
    [goalies]
  );
  const skaterRatingRange = skaterRatingRangeProp ?? computedSkaterRange;
  const goalieRatingRange = goalieRatingRangeProp ?? computedGoalieRange;

  const getTotalWishlistSalary = () =>
    allWishlist.reduce((total, player) => total + parseSalary(salaries, player, wishlistSalaries), 0);

  const getTotalLineupSalary = () => {
    const allLineup = [...lineupSkaters, ...lineupGoalieList];
    return (
      allLineup.reduce((total, player) => total + parseSalary(salaries, player, {}), 0) +
      (retainedSalary || 0)
    );
  };

  const removeFromWishlist = (player, isGoalie = false) => {
    if (isGoalie) {
      setWishlistGoalies((prev) => prev.filter((p) => p.id !== player.id));
    } else {
      setWishlistPlayers((prev) => prev.filter((p) => p.id !== player.id));
    }
    setWishlistSalaries((prev) => {
      const updated = { ...prev };
      delete updated[player.id];
      return updated;
    });
  };

  const getLineupPlayersByPosition = (wishlistPlayer) =>
    lineupSkaters.filter(
      (lineupPlayer) =>
        wishlistPlayer.positions &&
        lineupPlayer.positions &&
        wishlistPlayer.positions.some((pos) => lineupPlayer.positions.includes(pos))
    );

  const headToHeadPlayerA = allWishlist.find((p) => String(p.id) === headToHeadA);
  const headToHeadPlayerB = allWishlist.find((p) => String(p.id) === headToHeadB);
  const headToHeadBothGoalies =
    headToHeadPlayerA &&
    headToHeadPlayerB &&
    wishlistGoalies.some((g) => g.id === headToHeadPlayerA.id) &&
    wishlistGoalies.some((g) => g.id === headToHeadPlayerB.id);

  return (
    <div className="wishlist-page">
      <div className="wishlist-hero card">
        <div className="card-content">
          <h2>Free Agents</h2>
          <p>Build your target list, browse available free agents, and compare against your current lineup.</p>
        </div>
        <div className="wishlist-stats card-content">
          <div className="wishlist-stat">
            <span className="wishlist-stat-label">On wishlist</span>
            <span className="wishlist-stat-value">{allWishlist.length}</span>
          </div>
          <div className="wishlist-stat">
            <span className="wishlist-stat-label">Wishlist salary</span>
            <span className="wishlist-stat-value">{formatMoney(getTotalWishlistSalary())}</span>
          </div>
          <div className="wishlist-stat">
            <span className="wishlist-stat-label">Lineup salary</span>
            <span className="wishlist-stat-value">{formatMoney(getTotalLineupSalary())}</span>
          </div>
          <div className="wishlist-stat">
            <span className="wishlist-stat-label">Cap room</span>
            <span className="wishlist-stat-value">{formatMoney(Math.max(0, salaryCap - getTotalLineupSalary()))}</span>
          </div>
        </div>
      </div>

      <div className="wishlist-subtabs">
        <button
          type="button"
          className={`wishlist-subtab ${subTab === "list" ? "active" : ""}`}
          onClick={() => setSubTab("list")}
        >
          My List ({allWishlist.length})
        </button>
        <button
          type="button"
          className={`wishlist-subtab ${subTab === "browse" ? "active" : ""}`}
          onClick={() => setSubTab("browse")}
        >
          Browse Free Agents
        </button>
        <button
          type="button"
          className={`wishlist-subtab ${subTab === "compare" ? "active" : ""}`}
          onClick={() => setSubTab("compare")}
        >
          Compare
        </button>
      </div>

      {subTab === "list" && (
        <>
          {allWishlist.length === 0 ? (
            <div className="wishlist-empty card">
              <h3>Your wishlist is empty</h3>
              <p>Add free agents from the Browse Free Agents tab, or use the + List button on the Players tab.</p>
              <button type="button" className="btn btn-primary btn-sm" style={{ marginTop: "1rem" }} onClick={() => setSubTab("browse")}>
                Browse free agents
              </button>
            </div>
          ) : (
            <>
              {wishlistPlayers.length > 0 && (
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">Skaters ({wishlistPlayers.length})</h3>
                  </div>
                  <div className="card-content wishlist-grid">
                    {wishlistPlayers.map((player) => (
                      <WishlistCard
                        key={player.id}
                        player={player}
                        isGoalie={false}
                        wishlistSalaries={wishlistSalaries}
                        setWishlistSalaries={setWishlistSalaries}
                        onRemove={removeFromWishlist}
                        onGoToLineup={onGoToLineup}
                        onAddToLineup={onAddToLineup}
                        lineupAssignment={getLineupAssignmentDisplay(player, false)}
                        rateMin={skaterRatingRange.min}
                        rateMax={skaterRatingRange.max}
                      />
                    ))}
                  </div>
                </div>
              )}
              {wishlistGoalies.length > 0 && (
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">Goalies ({wishlistGoalies.length})</h3>
                  </div>
                  <div className="card-content wishlist-grid">
                    {wishlistGoalies.map((goalie) => (
                      <WishlistCard
                        key={goalie.id}
                        player={goalie}
                        isGoalie
                        wishlistSalaries={wishlistSalaries}
                        setWishlistSalaries={setWishlistSalaries}
                        onRemove={removeFromWishlist}
                        onGoToLineup={onGoToLineup}
                        onAddToLineup={onAddToLineup}
                        lineupAssignment={getLineupAssignmentDisplay(goalie, true)}
                        rateMin={goalieRatingRange.min}
                        rateMax={goalieRatingRange.max}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {subTab === "browse" && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Browse free agents</h3>
            <span style={{ fontSize: "0.875rem", color: "var(--gray-400)" }}>
              {browseList.length} available
            </span>
          </div>
          <div className="card-content">
            <div className="filters wishlist-browse-filters">
              <div className="form-group mb-0">
                <label className="form-label">Search</label>
                <div className="search-box">
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Search players..."
                    value={browseSearch}
                    onChange={(e) => setBrowseSearch(e.target.value)}
                  />
                  <svg className="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>

              <div className="form-group mb-0">
                <label className="form-label">Position</label>
                <select
                  className="form-select"
                  value={browsePosition}
                  onChange={(e) => {
                    const next = e.target.value;
                    setBrowsePosition(next);
                    if (next === "Goalie" && !goalieRateColumns.some((c) => c.name === browseSortColumn)) {
                      setBrowseSortColumn("OV");
                    } else if (
                      next !== "Goalie" &&
                      !rateColumns.includes(browseSortColumn) &&
                      !["name", "team", "age", "salary", "playerType"].includes(browseSortColumn)
                    ) {
                      setBrowseSortColumn("OV");
                    }
                  }}
                >
                  <option value="all">All Skaters</option>
                  <option value="Center">Center</option>
                  <option value="Right Wing">Right Wing</option>
                  <option value="Left Wing">Left Wing</option>
                  <option value="Defenseman">Defenseman</option>
                  <option value="Goalie">Goalie</option>
                </select>
              </div>

              <div className="form-group mb-0">
                <label className="form-label">Status</label>
                <select
                  className="form-select"
                  value={browseFaStatus}
                  onChange={(e) => setBrowseFaStatus(e.target.value)}
                >
                  <option value="all">All</option>
                  <option value="RFA">RFA</option>
                  <option value="UFA">UFA</option>
                </select>
              </div>

              {!isBrowsingGoalies && (
                <div className="form-group mb-0">
                  <label className="form-label">Type</label>
                  <select
                    className="form-select"
                    value={browsePlayerType}
                    onChange={(e) => setBrowsePlayerType(e.target.value)}
                  >
                    <option value="all">All Types</option>
                    <option value="T">T - Transport</option>
                    <option value="P">P - Playmaker</option>
                    <option value="S">S - Scorer</option>
                  </select>
                </div>
              )}

              <div className="form-group mb-0">
                <label className="form-label">Sort by</label>
                <select
                  className="form-select"
                  value={browseSortColumn}
                  onChange={(e) => setBrowseSortColumn(e.target.value)}
                >
                  {browseSortOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt === "playerType" ? "Type (T/P/S)" : opt}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group mb-0">
                <label className="form-label">Direction</label>
                <select
                  className="form-select"
                  value={browseSortDirection}
                  onChange={(e) => setBrowseSortDirection(e.target.value)}
                >
                  <option value="desc">High to Low</option>
                  <option value="asc">Low to High</option>
                </select>
              </div>
            </div>

            <div className="wishlist-browse-list">
              {browseList.map((player) => {
                const isGoalie = isBrowsingGoalies;
                const listed = isInWishlist(player, isGoalie);
                const lineupAssignment = getLineupAssignmentDisplay(player, isGoalie);
                return (
                  <div key={player.id} className={`wishlist-browse-row ${listed ? "in-list" : ""} ${lineupAssignment ? "in-lineup" : ""}`}>
                    <div className="wishlist-browse-row-top">
                      <div>
                        <div className="player-name-row player-name-row-inline">
                          <span className="player-name-text">{player.name}</span>
                          <PlayerNameBadges player={player} size="small" showPlayerType={!isGoalie} />
                        </div>
                        <div className="wishlist-card-meta">
                          <span>{isGoalie ? "G" : player.positionDisplay}</span>
                          <span>{formatMoney(player.salary)}</span>
                          {player.age ? <span>Age {player.age}</span> : null}
                          {player.OV ? <span>OV {player.OV}</span> : null}
                          {lineupAssignment ? <span className="wishlist-lineup-badge">Lineup: {lineupAssignment}</span> : null}
                        </div>
                      </div>
                      <div className="wishlist-browse-actions">
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={(e) => onAddToLineup(player, e)}
                        >
                          Add to Lineup
                        </button>
                        <button
                          type="button"
                          className={`btn btn-sm ${listed ? "btn-secondary" : "btn-primary"}`}
                          onClick={() => toggleWishlist(player, isGoalie)}
                        >
                          {listed ? "Remove from list" : "+ Add to list"}
                        </button>
                      </div>
                    </div>
                    <RatingStrip
                      player={player}
                      rateMin={isGoalie ? goalieRatingRange.min : skaterRatingRange.min}
                      rateMax={isGoalie ? goalieRatingRange.max : skaterRatingRange.max}
                      isGoalie={isGoalie}
                    />
                  </div>
                );
              })}
              {browseList.length === 0 && (
                <div className="wishlist-empty" style={{ padding: "2rem" }}>
                  <p>No free agents match your filters.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {subTab === "compare" && (
        <div className="compare-panel">
          {allWishlist.length === 0 ? (
            <div className="wishlist-empty card">
              <h3>Add players to compare</h3>
              <p>Add free agents to your wishlist first, then compare them here.</p>
            </div>
          ) : (
            <>
              <div className="compare-salary-bar">
                <div className="compare-salary-item">Lineup: {formatMoney(getTotalLineupSalary())}</div>
                <div className="compare-salary-item">Wishlist: {formatMoney(getTotalWishlistSalary())}</div>
                <div className="compare-salary-item">
                  Combined: {formatMoney(getTotalLineupSalary() + getTotalWishlistSalary())}
                </div>
                <div className="compare-salary-item">
                  vs Cap ({formatMoney(salaryCap)}):{" "}
                  {formatMoney(salaryCap - getTotalLineupSalary() - getTotalWishlistSalary())}
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Wishlist vs lineup</h3>
                </div>
                <div className="card-content">
                  {wishlistPlayers.map((wishlistPlayer) => {
                    const compatible = getLineupPlayersByPosition(wishlistPlayer);
                    const selected = compatible.find(
                      (p) => String(p.id) === String(selectedComparisons[wishlistPlayer.id])
                    );

                    return (
                      <div key={wishlistPlayer.id} className="compare-block" style={{ marginBottom: "1rem" }}>
                        <div className="compare-block-header">
                          <div className="player-name-row player-name-row-compare">
                            <span className="player-name-text">{wishlistPlayer.name}</span>
                            <PlayerNameBadges player={wishlistPlayer} />
                            <span className="player-name-compare-pos" style={{ color: "var(--gray-400)", fontSize: "0.875rem" }}>
                              ({wishlistPlayer.positionDisplay})
                            </span>
                          </div>
                          <div className="compare-select-group">
                            <label htmlFor={`cmp-${wishlistPlayer.id}`}>Compare with lineup:</label>
                            <select
                              id={`cmp-${wishlistPlayer.id}`}
                              className="compare-select"
                              value={selectedComparisons[wishlistPlayer.id] || ""}
                              onChange={(e) =>
                                setSelectedComparisons((prev) => ({
                                  ...prev,
                                  [wishlistPlayer.id]: e.target.value,
                                }))
                              }
                            >
                              <option value="">Select player</option>
                              {compatible.map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.name} ({p.positionDisplay})
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {selected ? (
                          <>
                            <div className="compare-player-row wishlist">
                              Wishlist · {wishlistPlayer.name} · {formatMoney(parseSalary(salaries, wishlistPlayer, wishlistSalaries))}
                            </div>
                            <RatingStrip
                              player={wishlistPlayer}
                              rateMin={skaterRatingRange.min}
                              rateMax={skaterRatingRange.max}
                            />
                            <div className="compare-player-row lineup">
                              Lineup · {selected.name} · {formatMoney(parseSalary(salaries, selected, {}))}
                            </div>
                            <RatingStrip
                              player={selected}
                              rateMin={skaterRatingRange.min}
                              rateMax={skaterRatingRange.max}
                            />
                            <div className="compare-player-row" style={{ marginTop: "0.75rem" }}>
                              Difference (wishlist − lineup) · Salary:{" "}
                              {formatMoney(
                                parseSalary(salaries, wishlistPlayer, wishlistSalaries) -
                                  parseSalary(salaries, selected, {})
                              )}
                            </div>
                            <DiffStrip playerA={wishlistPlayer} playerB={selected} />
                          </>
                        ) : compatible.length === 0 ? (
                          <p style={{ color: "var(--gray-400)", fontSize: "0.875rem", margin: 0 }}>
                            No lineup players at {wishlistPlayer.positionDisplay} to compare.
                          </p>
                        ) : null}
                      </div>
                    );
                  })}

                  {wishlistGoalies.length > 0 && lineupGoalieList.length > 0 &&
                    wishlistGoalies.map((wishlistGoalie) => {
                      const selected = lineupGoalieList.find(
                        (g) => String(g.id) === String(selectedComparisons[`g-${wishlistGoalie.id}`])
                      );
                      return (
                        <div key={wishlistGoalie.id} className="compare-block" style={{ marginBottom: "1rem" }}>
                          <div className="compare-block-header">
                            <strong>{wishlistGoalie.name}</strong>
                            <div className="compare-select-group">
                              <label htmlFor={`cmp-g-${wishlistGoalie.id}`}>Compare with:</label>
                              <select
                                id={`cmp-g-${wishlistGoalie.id}`}
                                className="compare-select"
                                value={selectedComparisons[`g-${wishlistGoalie.id}`] || ""}
                                onChange={(e) =>
                                  setSelectedComparisons((prev) => ({
                                    ...prev,
                                    [`g-${wishlistGoalie.id}`]: e.target.value,
                                  }))
                                }
                              >
                                <option value="">Select goalie</option>
                                {lineupGoalieList.map((g) => (
                                  <option key={g.id} value={g.id}>
                                    {g.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          {selected && (
                            <>
                              <div className="compare-player-row wishlist">
                                Wishlist · {wishlistGoalie.name} · {formatMoney(parseSalary(salaries, wishlistGoalie, wishlistSalaries))}
                              </div>
                              <RatingStrip
                                player={wishlistGoalie}
                                rateMin={goalieRatingRange.min}
                                rateMax={goalieRatingRange.max}
                                isGoalie
                              />
                              <div className="compare-player-row lineup">
                                Lineup · {selected.name} · {formatMoney(parseSalary(salaries, selected, {}))}
                              </div>
                              <RatingStrip
                                player={selected}
                                rateMin={goalieRatingRange.min}
                                rateMax={goalieRatingRange.max}
                                isGoalie
                              />
                              <div className="compare-player-row" style={{ marginTop: "0.75rem" }}>
                                Difference · Salary:{" "}
                                {formatMoney(
                                  parseSalary(salaries, wishlistGoalie, wishlistSalaries) -
                                    parseSalary(salaries, selected, {})
                                )}
                              </div>
                              <DiffStrip playerA={wishlistGoalie} playerB={selected} isGoalie />
                            </>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>

              {allWishlist.length >= 2 && (
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">Head-to-head (wishlist vs wishlist)</h3>
                  </div>
                  <div className="card-content">
                    <div className="compare-select-group" style={{ marginBottom: "1rem" }}>
                      <select className="compare-select" value={headToHeadA} onChange={(e) => setHeadToHeadA(e.target.value)}>
                        <option value="">Player A</option>
                        {allWishlist.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                      <select className="compare-select" value={headToHeadB} onChange={(e) => setHeadToHeadB(e.target.value)}>
                        <option value="">Player B</option>
                        {allWishlist.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {headToHeadPlayerA && headToHeadPlayerB && headToHeadA !== headToHeadB && (
                      <div className="head-to-head-grid">
                        <div>
                          <div className="compare-player-row wishlist">{headToHeadPlayerA.name}</div>
                          <RatingStrip
                            player={headToHeadPlayerA}
                            rateMin={headToHeadBothGoalies ? goalieRatingRange.min : skaterRatingRange.min}
                            rateMax={headToHeadBothGoalies ? goalieRatingRange.max : skaterRatingRange.max}
                            isGoalie={headToHeadBothGoalies}
                          />
                          <p style={{ fontSize: "0.8125rem", marginTop: "0.5rem" }}>
                            {formatMoney(parseSalary(salaries, headToHeadPlayerA, wishlistSalaries))}
                          </p>
                        </div>
                        <div className="head-to-head-vs">vs</div>
                        <div>
                          <div className="compare-player-row wishlist">{headToHeadPlayerB.name}</div>
                          <RatingStrip
                            player={headToHeadPlayerB}
                            rateMin={headToHeadBothGoalies ? goalieRatingRange.min : skaterRatingRange.min}
                            rateMax={headToHeadBothGoalies ? goalieRatingRange.max : skaterRatingRange.max}
                            isGoalie={headToHeadBothGoalies}
                          />
                          <p style={{ fontSize: "0.8125rem", marginTop: "0.5rem" }}>
                            {formatMoney(parseSalary(salaries, headToHeadPlayerB, wishlistSalaries))}
                          </p>
                        </div>
                      </div>
                    )}

                    {headToHeadPlayerA && headToHeadPlayerB && headToHeadA !== headToHeadB && (
                      <div style={{ marginTop: "1.25rem" }}>
                        <div className="compare-player-row">
                          Difference (A − B) · Salary:{" "}
                          {formatMoney(
                            parseSalary(salaries, headToHeadPlayerA, wishlistSalaries) -
                              parseSalary(salaries, headToHeadPlayerB, wishlistSalaries)
                          )}
                        </div>
                        <DiffStrip
                          playerA={headToHeadPlayerA}
                          playerB={headToHeadPlayerB}
                          isGoalie={headToHeadBothGoalies}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
